import { Controller } from "@hotwired/stimulus"
import { bufferToBase64url, base64urlToBuffer, csrfHeaders } from "./webauthn_codec"

// Adding a passkey to the signed-in user (docs/autenticacio.md).
export default class extends Controller {
  static values = {
    beginUrl: String,
    finishUrl: String,
    unsupportedMessage: String,
    creatingText: String,
    failedMessage: String
  }
  static targets = ["button", "error"]

  async register(event) {
    event.preventDefault()

    if (!window.PublicKeyCredential) {
      this.showError(this.unsupportedMessageValue)
      return
    }

    const button = this.buttonTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = this.creatingTextValue

    try {
      const optionsResponse = await fetch(this.beginUrlValue, { method: "POST", headers: csrfHeaders() })
      const options = await optionsResponse.json()

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: base64urlToBuffer(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          attestation: options.attestation || "none",
          authenticatorSelection: options.authenticatorSelection || {
            residentKey: "preferred",
            userVerification: "preferred"
          },
          excludeCredentials: (options.excludeCredentials || []).map((cred) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type,
            transports: cred.transports
          }))
        }
      })

      const finishResponse = await fetch(this.finishUrlValue, {
        method: "POST",
        headers: csrfHeaders(),
        body: JSON.stringify({
          credential: {
            id: credential.id,
            type: credential.type,
            rawId: bufferToBase64url(credential.rawId),
            authenticatorAttachment: credential.authenticatorAttachment,
            response: {
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
              attestationObject: bufferToBase64url(credential.response.attestationObject),
              transports: credential.response.getTransports ? credential.response.getTransports() : []
            }
          }
        })
      })
      const result = await finishResponse.json()

      if (!finishResponse.ok || !result.success) throw new Error(result.error || this.failedMessageValue)
      window.location.href = result.redirect_to
    } catch (error) {
      // NotAllowedError is the user cancelling the browser dialog
      if (error.name !== "NotAllowedError") this.showError(error.message)
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }
}
